import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Prüfe ob gespeicherte Tokens existieren
    const accessToken = localStorage.getItem('accessToken');
    const refreshTokenStored = localStorage.getItem('refreshToken');
    const userData = localStorage.getItem('user');
    
    if (accessToken && userData) {
      try {
        const user = JSON.parse(userData);
        setUser(user);
        
        // Versuche, den Token zu validieren (entscheidend!)
        // Wenn der accessToken abgelaufen ist, refreshe ihn
        try {
          // Decode JWT ohne Validierung um expiresIn zu prüfen
          const tokenParts = accessToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const expiresAt = payload.exp * 1000; // in Millisekunden
            const now = Date.now();
            
            // Wenn Token in weniger als 1 Minute abläuft, sofort refreshen
            if (expiresAt - now < 60000 && refreshTokenStored) {
              performRefresh();
            }
          }
        } catch (tokenCheckError) {
          // Token-Parsing fehlgeschlagen? Versuche zu refreshen
          if (refreshTokenStored) {
            performRefresh();
          }
        }
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    }
    setLoading(false);
  }, []);

  const login = async (identifier, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier, password })
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || 'Login fehlgeschlagen');
    }

    const data = await res.json();
    localStorage.setItem('accessToken', data.tokens.accessToken);
    localStorage.setItem('refreshToken', data.tokens.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (username, email, birthdate, password) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, birthdate, password })
    });

    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || 'Registrierung fehlgeschlagen');
    }

    const data = await res.json();

    // New flow: Email verification required
    if (data.requiresVerification) {
      return {
        success: true,
        requiresVerification: true,
        email: data.email,
        message: data.message
      };
    }

    // Old flow: Direct login (fallback, shouldn't happen with current setup)
    if (data.tokens && data.user) {
      localStorage.setItem('accessToken', data.tokens.accessToken);
      localStorage.setItem('refreshToken', data.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    }

    throw new Error('Unexpected response format');
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (e) {
      // Ignoriere Fehler beim Logout
    }
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshToken = async () => {
    const refreshTokenStored = localStorage.getItem('refreshToken');
    if (!refreshTokenStored) {
      logout();
      return null;
    }

    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refreshTokenStored })
      });

      if (!res.ok) {
        logout();
        return null;
      }

      const data = await res.json();
      localStorage.setItem('accessToken', data.tokens.accessToken);
      localStorage.setItem('refreshToken', data.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      return data.tokens.accessToken;
    } catch (e) {
      logout();
      return null;
    }
  };

  // Hilfsunktion für Refresh beim App-Start
  const performRefresh = async () => {
    const refreshTokenStored = localStorage.getItem('refreshToken');
    if (!refreshTokenStored) {
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      return;
    }

    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refreshTokenStored })
      });

      if (!res.ok) {
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
        return;
      }

      const data = await res.json();
      localStorage.setItem('accessToken', data.tokens.accessToken);
      localStorage.setItem('refreshToken', data.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
    } catch (e) {
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  };

  const fetchWithAuth = async (url, options = {}) => {
    let token = localStorage.getItem('accessToken');
    
    const res = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    });

    // Token abgelaufen? Refresh und retry
    if (res.status === 401) {
      const newToken = await refreshToken();
      if (newToken) {
        return fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`
          }
        });
      }
    }

    return res;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    fetchWithAuth,
    isAdmin: user?.roles?.includes('admin') || false,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
